import {
  collectAssetFields,
  formatAssetDisplayName,
  hasAssetTypeName
} from '@sollapay/domain/asset-types';

import type {AssetDisplayNameCopy, AssetFieldKey} from '@sollapay/domain/asset-types';
import type {AssetType} from '@sollapay/enums';
import type {AssetSummaryDTO} from '@sollapay/types';
import type {TFunction} from 'i18next';

const HEADER = 'view.buyers.detail.assetHeader';

export interface AssetLabelPart {
  key: AssetFieldKey;
  label: string;
  value: string;
}

/** Descriptor field order puts the most identifying field first. Empty on an unrecognized assetType. */
export function getAssetLabelParts(
  asset: AssetSummaryDTO,
  t: TFunction,
  limit?: number
): AssetLabelPart[] {
  return collectAssetFields(asset.assetType, asset, {limit}).map(entry => ({
    key: entry.key,
    label: t(`view.buyers.detail.assetFields.${entry.labelToken}`),
    value: entry.value
  }));
}

/** Free text that reads as itself — "כתובת תל מונד מזרח" says nothing "תל מונד מזרח" doesn't. */
const SELF_DESCRIBING_FIELDS = new Set<AssetFieldKey>(['address', 'description']);

function renderPart(part: AssetLabelPart, value: string): string {
  return SELF_DESCRIBING_FIELDS.has(part.key) ? value : `${part.label} ${value}`;
}

/** Empty for a type whose numbers identify it, so callers never lead with a name it doesn't have. */
export function getAssetTypeName(assetType: AssetType, t: TFunction): string {
  if (!hasAssetTypeName(assetType)) return '';
  return t(`view.buyers.assetTypes.${assetType}`, {defaultValue: ''});
}

function assembleLabel(typeName: string, renderedParts: string[], displayCode: string): string {
  return [typeName, ...renderedParts].filter(Boolean).join(' · ') || displayCode;
}

/** The display chokepoint — call sites never inspect assetType; falls back to displayCode when nothing resolves. */
export function formatAssetLabel(asset: AssetSummaryDTO, t: TFunction): string {
  const renderedParts = getAssetLabelParts(asset, t).map(part => renderPart(part, part.value));
  return assembleLabel(getAssetTypeName(asset.assetType, t), renderedParts, asset.displayCode);
}

/** Resolves title copy for `resolveAssetDisplayName`/`formatAssetDisplayName` from the shared locale keys. */
export function buildAssetDisplayNameCopy(t: TFunction): AssetDisplayNameCopy {
  return {
    typeName: assetType => getAssetTypeName(assetType, t),
    title: (source, [first = '', second = '']) => {
      switch (source) {
        case 'address':
        case 'description':
          return first;
        case 'developerUnit':
          return t(`${HEADER}.titleDeveloperUnit`, {building: first, unit: second});
        case 'registry':
          return t(`${HEADER}.titleRegistry`, {block: first, parcel: second});
      }
    }
  };
}

/** The short, meaningful name — type plus its resolved title — for table cells, pills, subtitles, and dropdown options. */
export function formatAssetName(asset: AssetSummaryDTO, t: TFunction): string {
  return formatAssetDisplayName(asset, buildAssetDisplayNameCopy(t));
}

/** A purchase's assets as one cell/pill/option label — each named, comma-joined; just the name for a single asset. */
export function formatPurchaseAssetNames(assets: AssetSummaryDTO[], t: TFunction): string {
  return assets.map(asset => formatAssetName(asset, t)).join(', ');
}

/** Long-form counterpart of `formatPurchaseAssetNames`, for exports and search. */
export function formatPurchaseAssetLabels(assets: AssetSummaryDTO[], t: TFunction): string {
  return assets.map(asset => formatAssetLabel(asset, t)).join(', ');
}

/** Feed-row subtitle: null for a single-asset trust type (general sale) or when there's no asset to name. */
export function getAssetFeedSubtitle(
  asset: AssetSummaryDTO | null | undefined,
  t: TFunction,
  allowsMultipleAssets: boolean
): string | null {
  return allowsMultipleAssets && asset ? formatAssetName(asset, t) : null;
}
